import { useCallback, useState } from 'react'

import type { CustomerContractDescription } from '@orus.eu/backend'

import { coBrandingFactories } from '../embedding-partner.ts'

type EmbeddingPartnerSelection = { type: 'default' } | { type: 'unbranded' } | { type: 'partner'; id: string }

/**
 * Which embedding partner the app should brand for: an explicit partner, the unbranded Orus funnel,
 * or the default derived from the URL (anonymous sessions) and the customer's first contract.
 */
export function useEmbeddingPartnerSelection(contracts: CustomerContractDescription[] | undefined): {
  embeddingPartnerId: string | undefined
  setEmbeddingPartnerId: (embeddingPartnerId: string | undefined) => void
  forceOrusDefault: () => void
} {
  const [selection, setSelection] = useState<EmbeddingPartnerSelection>(() => {
    const initialId = getInitialEmbeddingPartnerId()
    return initialId ? { type: 'partner', id: initialId } : { type: 'default' }
  })

  const setEmbeddingPartnerId = useCallback(
    (id: string | undefined) => setSelection(id ? { type: 'partner', id } : { type: 'default' }),
    [],
  )
  const forceOrusDefault = useCallback(() => setSelection({ type: 'unbranded' }), [])

  return {
    embeddingPartnerId: resolveEmbeddingPartnerId(selection, contracts),
    setEmbeddingPartnerId,
    forceOrusDefault,
  }
}

function resolveEmbeddingPartnerId(
  selection: EmbeddingPartnerSelection,
  contracts: CustomerContractDescription[] | undefined,
): string | undefined {
  switch (selection.type) {
    case 'partner':
      return selection.id
    case 'unbranded':
      return undefined
    case 'default':
      return getDefaultEmbeddingPartnerId(contracts)
  }
}

/**
 * Gets the default embedding partner id, based on the presence or absence of a contract, to use until
 * a different embedding partner id is explicitly set.
 */
function getDefaultEmbeddingPartnerId(contracts: CustomerContractDescription[] | undefined): string | undefined {
  if (!contracts) {
    // loading, or failed and waiting for redirect
    return getInitialEmbeddingPartnerId()
  }

  if (contracts.length < 1) {
    // no contract => no embedding partner
    return undefined
  }

  const firstContract = contracts[0]
  const embeddingPartner = firstContract.embeddingPartner
  return embeddingPartner === 'none' ? undefined : embeddingPartner.id
}

/**
 * Gets the initial embedding partner id from the URL parameters, to avoid a flash while loading
 */
function getInitialEmbeddingPartnerId(): string | undefined {
  const url = new URL(document.location.href)

  const embeddingPartnerParameter = url.searchParams.get('embedding_partner')
  if (embeddingPartnerParameter && embeddingPartnerParameter in coBrandingFactories) {
    return embeddingPartnerParameter
  }

  return undefined
}
